import { useEffect, useState } from 'react';

import { formatRetryTime } from './LLMClient';
import { formatRateLimitMessage, getTimeUntilRetry, isRateLimited } from './rateLimitStore';

export interface RateLimitStatus {
    isRateLimited: boolean;
    timeRemaining: number | null;
    message: string | null;
}

/**
 * React hook that provides rate limit status with automatic updates.
 * Checks localStorage for stored rate limit and updates every second.
 * This hook drives user-facing UI (e.g. ChatInput), so it checks the interactive tier.
 */
export function useRateLimitStatus(): RateLimitStatus {
    const [rateLimited, setRateLimited] = useState(() => isRateLimited('interactive'));
    const [timeRemaining, setTimeRemaining] = useState<number | null>(() =>
        isRateLimited('interactive') ? getTimeUntilRetry('interactive') : null,
    );

    useEffect(() => {
        const check = () => {
            const limited = isRateLimited('interactive');
            setRateLimited(limited);
            setTimeRemaining(limited ? getTimeUntilRetry('interactive') : null);
        };

        check();
        const interval = setInterval(check, 1000);
        return () => clearInterval(interval);
    }, []);

    const message =
        timeRemaining !== null
            ? formatRateLimitMessage(Math.ceil(timeRemaining / 1000), formatRetryTime)
            : null;

    return { isRateLimited: rateLimited, timeRemaining, message };
}
